import React, { useCallback, useMemo, useState, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, DownloadIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, FileSpreadsheetIcon, XIcon } from 'lucide-react';
// Template columns matching the Supplier CRUD fields
const TEMPLATE_COLUMNS = [{
  key: 'supplierType',
  label: 'Supplier Type',
  hint: 'company or individual',
  required: true
}, {
  key: 'name',
  label: 'Name',
  hint: 'Company or individual name',
  required: true
}, {
  key: 'email',
  label: 'Email',
  hint: 'Contact email',
  required: true
}, {
  key: 'phone',
  label: 'Phone',
  hint: 'Phone number',
  required: false
}, {
  key: 'taxId',
  label: 'Tax ID',
  hint: '13-digit tax ID',
  required: false
}, {
  key: 'companyNameMs',
  label: 'Company Name (MS)',
  hint: 'Malay name',
  required: false
}, {
  key: 'companyNameEn',
  label: 'Company Name (EN)',
  hint: 'English name',
  required: false
}, {
  key: 'branch',
  label: 'Branch',
  hint: 'e.g. 00000',
  required: false
}, {
  key: 'vatRegistered',
  label: 'VAT Registered',
  hint: 'true or false',
  required: false
}, {
  key: 'contactPerson',
  label: 'Contact Person',
  hint: 'Contact name',
  required: false
}, {
  key: 'addressLine1',
  label: 'Address Line 1',
  hint: 'Street address',
  required: false
}, {
  key: 'addressLine2',
  label: 'Address Line 2',
  hint: 'District',
  required: false
}, {
  key: 'city',
  label: 'City',
  hint: 'City name',
  required: false
}, {
  key: 'province',
  label: 'Province',
  hint: 'Province',
  required: false
}, {
  key: 'postcode',
  label: 'Postcode',
  hint: 'Postal code',
  required: false
}, {
  key: 'internalReference',
  label: 'Internal Reference',
  hint: 'Your internal ref',
  required: false
}, {
  key: 'payoutMethod',
  label: 'Payout Method',
  hint: 'bank_account or promptpay',
  required: false
}, {
  key: 'bankName',
  label: 'Bank Name',
  hint: 'Bank name',
  required: false
}, {
  key: 'accountNumber',
  label: 'Account Number',
  hint: 'Bank account number',
  required: false
}, {
  key: 'accountName',
  label: 'Account Name',
  hint: 'Account holder name',
  required: false
}];
interface ParsedRow {
  id: string;
  data: Record<string, string>;
  errors: string[];
}
function generateCSVTemplate(): string {
  const header = TEMPLATE_COLUMNS.map(c => c.label).join(',');
  const sampleRow = ['company', 'Acme Corp Ltd', 'contact@acmecorp.com', '021234567', '0105563012345', 'Acme Pvt Ltd', 'Acme Pvt Ltd', '00000', 'true', 'Ahmed Saleem', 'H. Maaveyo, Boduthakurufaanu Magu', 'Henveiru', 'Malé', 'Malé', '20094', 'SUP-NEW', 'bank_account', 'Bank of Maldives', '0381236789', 'Acme Corp Pvt. Ltd.'].join(',');
  return `${header}\n${sampleRow}`;
}
function parseCSV(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return {
    headers: [],
    rows: []
  };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
  return {
    headers,
    rows
  };
}
function mapHeaderToKey(header: string): string | null {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const col of TEMPLATE_COLUMNS) {
    const colNorm = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === colNorm) return col.key;
    const keyNorm = col.key.toLowerCase();
    if (normalized === keyNorm) return col.key;
  }
  return null;
}
function validateRow(data: Record<string, string>): string[] {
  const errors: string[] = [];
  if (!data.name) errors.push('Name is required');
  if (!data.email) errors.push('Email is required');
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Invalid email format');
  }
  if (!data.supplierType) {
    errors.push('Supplier type is required');
  } else if (!['company', 'individual'].includes(data.supplierType.toLowerCase())) {
    errors.push('Supplier type must be "company" or "individual"');
  }
  if (data.payoutMethod && !['bank_account', 'promptpay'].includes(data.payoutMethod.toLowerCase())) {
    errors.push('Payout method must be "bank_account" or "promptpay"');
  }
  return errors;
}
export function BulkSupplierUpload() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const validRows = useMemo(() => parsedRows.filter(r => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter(r => r.errors.length > 0), [parsedRows]);
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supplier_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const processFile = useCallback((file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const {
        headers,
        rows
      } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.');
        return;
      }
      // Map headers to keys
      const keyMap: (string | null)[] = headers.map(mapHeaderToKey);
      const unmapped = headers.filter((_, i) => keyMap[i] === null);
      if (unmapped.length > 0 && keyMap.filter(Boolean).length === 0) {
        setParseError(`Could not match any columns. Please use the template format. Unrecognized headers: ${unmapped.join(', ')}`);
        return;
      }
      const parsed: ParsedRow[] = rows.map((values, idx) => {
        const data: Record<string, string> = {};
        keyMap.forEach((key, i) => {
          if (key && values[i] !== undefined) {
            data[key] = values[i];
          }
        });
        return {
          id: `row-${idx}`,
          data,
          errors: validateRow(data)
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const removeRow = (id: string) => {
    setParsedRows(prev => prev.filter(r => r.id !== id));
  };
  const handleImport = () => {
    // In a real app, this would POST to an API
    setStep('success');
  };
  const handleReset = () => {
    setStep('upload');
    setParsedRows([]);
    setFileName('');
    setParseError('');
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-12727">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-12728">
        <div className="flex items-center space-x-4" data-id="element-12729">
          <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-12730">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-12731" />
            {t('pages.BulkSupplierUpload.back_to_suppliers')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12732">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-12733">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-12734">
            {t('pages.BulkSupplierUpload.bulk_upload_suppliers')}
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-12735">
            {t('pages.BulkSupplierUpload.upload_a_csv_file_to_add')}
          </p>
        </div>

        <div className="p-8" data-id="element-12736">
          {/* ========== UPLOAD STEP ========== */}
          {step === 'upload' && <div className="space-y-8" data-id="element-12737">
              {/* Template download */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-center justify-between" data-id="element-12738">
                <div className="flex items-center" data-id="element-12739">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4" data-id="element-12740">
                    <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-12741" />
                  </div>
                  <div data-id="element-12742">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12743">
                      {t('pages.BulkSupplierUpload.download_template')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-12744">
                      {t('pages.BulkSupplierUpload.use_this_csv_template_to_format')}
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#9F140F] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-12745">
                  <DownloadIcon size={16} className="mr-2" data-id="element-12746" />
                  {t('pages.BulkSupplierUpload.download_csv')}
                </button>
              </div>

              {/* Dropzone */}
              <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging ? 'border-[#E2231A] bg-[#E2231A]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-12747">
                <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-12748" />

                <div className="flex flex-col items-center" data-id="element-12749">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#E2231A]/10' : 'bg-gray-100'}`} data-id="element-12750">
                    <UploadCloudIcon size={28} className={isDragging ? 'text-[#9F140F]' : 'text-gray-400'} data-id="element-12751" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-12752">
                    {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-12753">
                    {t('pages.BulkSupplierUpload.or_click_to_browse')}
                  </p>
                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-12754">
                    {t('pages.BulkSupplierUpload.supported_csv')}
                  </span>
                </div>
              </div>

              {parseError && <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-12755">
                  <AlertCircleIcon size={18} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-12756" />

                  <div data-id="element-12757">
                    <p className="text-sm font-medium text-red-800" data-id="element-12758">
                      {t('pages.BulkSupplierUpload.upload_error')}
                    </p>
                    <p className="text-xs text-red-600 mt-0.5" data-id="element-12759">{parseError}</p>
                  </div>
                </div>}

              {/* Field reference */}
              <div data-id="element-12760">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-12761">
                  {t('pages.BulkSupplierUpload.expected_fields')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-12762">
                  {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-12763">
                      <span className="font-medium text-gray-800 mr-1" data-id="element-12764">
                        {col.label}
                      </span>
                      {col.required && <span className="text-red-400 text-[10px]" data-id="element-12765">*</span>}
                    </div>)}
                </div>
              </div>
            </div>}

          {/* ========== PREVIEW STEP ========== */}
          {step === 'preview' && <div className="space-y-6" data-id="element-12766">
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-5 py-4" data-id="element-12767">
                <div className="flex items-center space-x-6" data-id="element-12768">
                  <div className="flex items-center text-sm" data-id="element-12769">
                    <FileSpreadsheetIcon size={16} className="text-gray-400 mr-2" data-id="element-12770" />

                    <span className="font-medium text-gray-900" data-id="element-12771">
                      {fileName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm" data-id="element-12772">
                    <span className="text-gray-600" data-id="element-12773">
                      <span className="font-semibold text-gray-900" data-id="element-12774">
                        {parsedRows.length}
                      </span>{' '}
                      {t('pages.BulkSupplierUpload.rows_total')}
                    </span>
                    <span className="text-green-600" data-id="element-12775">
                      <span className="font-semibold" data-id="element-12776">{validRows.length}</span>{' '}
                      {t('pages.BulkSupplierUpload.valid')}
                    </span>
                    {errorRows.length > 0 && <span className="text-red-600" data-id="element-12777">
                        <span className="font-semibold" data-id="element-12778">
                          {errorRows.length}
                        </span>{' '}
                        {t('pages.BulkSupplierUpload.with_errors')}
                      </span>}
                  </div>
                </div>
                <button onClick={handleReset} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-12779">
                  {t('pages.BulkSupplierUpload.upload_different_file')}
                </button>
              </div>

              {/* Error rows */}
              {errorRows.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-12780">
                  <div className="flex items-center mb-3" data-id="element-12781">
                    <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-12782" />
                    <span className="text-sm font-medium text-red-800" data-id="element-12783">
                      {errorRows.length} {t('pages.BulkSupplierUpload.row')}
                      {errorRows.length > 1 ? 's' : ''}{' '}
                      {t('pages.BulkSupplierUpload.with_validation_errors')}
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mb-3" data-id="element-12784">
                    {t('pages.BulkSupplierUpload.these_rows_have_issues_and_will')}
                  </p>
                  <div className="space-y-2" data-id="element-12785">
                    {errorRows.map(row => <div key={row.id} className="flex items-center justify-between bg-white rounded-md border border-red-200 px-4 py-2.5" data-id="element-12786">
                        <div className="flex-1 min-w-0" data-id="element-12787">
                          <span className="text-sm font-medium text-gray-900 mr-3" data-id="element-12788">
                            {row.data.name || '(no name)'}
                          </span>
                          <span className="text-xs text-gray-500 mr-3" data-id="element-12789">
                            {row.data.email || '(no email)'}
                          </span>
                          <span className="text-xs text-red-600" data-id="element-12790">
                            {row.errors.join('; ')}
                          </span>
                        </div>
                        <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors ml-3" data-id="element-12791">
                          <TrashIcon size={14} data-id="element-12792" />
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Preview table */}
              {validRows.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-12793">
                  <div className="overflow-x-auto" data-id="element-12794">
                    <table className="w-full text-left" data-id="element-12795">
                      <thead data-id="element-12796">
                        <tr className="bg-gray-50 border-b border-gray-200" data-id="element-12797">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10" data-id="element-12798">
                            #
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12799">
                            {t('pages.BulkSupplierUpload.type')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12800">
                            {t('pages.BulkSupplierUpload.name')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12801">
                            {t('pages.BulkSupplierUpload.email')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12802">
                            {t('pages.BulkSupplierUpload.phone')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12803">
                            {t('pages.BulkSupplierUpload.tax_id')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12804">
                            {t('pages.BulkSupplierUpload.province')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-12805">
                            {t('pages.BulkSupplierUpload.payout')}
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12" data-id="element-12806"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-12807">
                        {validRows.map((row, idx) => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-12808">
                            <td className="px-4 py-3 text-xs text-gray-400" data-id="element-12809">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3" data-id="element-12810">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.data.supplierType?.toLowerCase() === 'company' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`} data-id="element-12811">
                                {row.data.supplierType || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-12812">
                              {row.data.name || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12813">
                              {row.data.email || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12814">
                              {row.data.phone || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs" data-id="element-12815">
                              {row.data.taxId || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12816">
                              {row.data.province || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-12817">
                              {row.data.payoutMethod || '-'}
                            </td>
                            <td className="px-4 py-3" data-id="element-12818">
                              <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors" data-id="element-12819">
                                <TrashIcon size={14} data-id="element-12820" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {parsedRows.length === 0 && <div className="text-center py-12 text-gray-500 text-sm" data-id="element-12821">
                  {t('pages.BulkSupplierUpload.all_rows_have_been_removed_upload')}
                </div>}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-12822">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-12823">
                  {t('pages.BulkSupplierUpload.cancel')}
                </button>
                <button onClick={handleImport} disabled={validRows.length === 0} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${validRows.length > 0 ? 'bg-[#E2231A] hover:bg-[#C41B14] text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-12824">
                  {t('pages.BulkSupplierUpload.import')} {validRows.length}{' '}
                  {t('pages.BulkSupplierUpload.supplier')}
                  {validRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && <div className="text-center py-16" data-id="element-12825">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-12826">
                <CheckCircleIcon size={32} className="text-green-600" data-id="element-12827" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-12828">
                {t('pages.BulkSupplierUpload.suppliers_imported_successfully')}
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-12829">
                {validRows.length} {t('pages.BulkSupplierUpload.supplier')}
                {validRows.length !== 1 ? 's have' : ' has'}{' '}
                {t('pages.BulkSupplierUpload.been_added_to_your_account')}
              </p>
              <div className="flex justify-center gap-3" data-id="element-12830">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-12831">
                  {t('pages.BulkSupplierUpload.upload_more')}
                </button>
                <button onClick={() => navigate('/suppliers')} className="px-5 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12832">
                  {t('pages.BulkSupplierUpload.go_to_suppliers')}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}